import { useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import { BannerButton, IconButtonV2, Layout, TextArea } from '@utilities';
import { ArrowRightIcon, GridRightIcon, NexaLogoIcon } from '@utilities/icons';

import Footer from '../../../../../../components/Footer';
import { CHAT_INPUT_DISCLAIMER } from '../../utilities/constants';
import { getChatMessageValidation } from '../../utilities/helpers';
import NarrativesChatInputFooter, {
  NARRATIVES_CHAT_INPUT_ERROR_ID,
} from '../NarrativesChatInputFooter';

import styles from './_index.module.scss';

const NarrativesChatLandingPage = ({
  capacityTracker,
  isCapacityExhausted,
  onNavigation,
  onSubmit,
}) => {
  const [inputValue, setInputValue] = useState('');

  const { hasInputText, isInputTooLong, isValid, trimmedValue } =
    getChatMessageValidation(inputValue);

  const handleChangeInput = (value) => setInputValue(value);

  const handleSubmit = () => {
    if (!isValid || isCapacityExhausted) return;

    onSubmit(trimmedValue);
    onNavigation('chat');
  };

  const handleInputKeyDown = (event) => {
    if (event.key !== 'Enter' || event.shiftKey) return;

    event.preventDefault();
    handleSubmit();
  };

  return (
    <Layout.Column className={styles['chat-landing-page']}>
      <div className={styles['chat-landing-page-content']}>
        <div className={styles['chat-header-branding']}>
          <div className={styles['nexa-landing-brand-mark']}>
            <NexaLogoIcon />
          </div>
        </div>
        <h1 className={styles['chat-header']}>What business question do you have?</h1>
        <h2 className={styles['chat-subheader']}>Every narrative begins with a question.</h2>
        <p className={styles['chat-input-disclaimer']}>{CHAT_INPUT_DISCLAIMER}</p>
        <div
          className={classNames(styles['chat-input-field'], {
            [styles['chat-input-field-invalid']]: isInputTooLong,
          })}
          data-testid="chat-input-field"
        >
          <div className={styles['chat-input-textarea']}>
            <TextArea
              aria-describedby={isInputTooLong ? NARRATIVES_CHAT_INPUT_ERROR_ID : undefined}
              aria-invalid={isInputTooLong}
              autoFocus
              autoGrow
              className={styles['chat-input-control']}
              onChange={handleChangeInput}
              onKeyDown={handleInputKeyDown}
              placeholder="What would you like to know?"
              rows={1}
              value={inputValue}
            />
          </div>

          <span
            className={classNames(styles['chat-input-submit'], {
              [styles['chat-input-submit-disabled']]:
                hasInputText && (isCapacityExhausted || isInputTooLong),
              [styles['chat-input-submit-filled']]: hasInputText,
            })}
            data-testid="chat-input-submit-wrapper"
          >
            <IconButtonV2
              aria-label="Submit prompt"
              className={styles['chat-input-submit-button']}
              disabled={!hasInputText || isCapacityExhausted || isInputTooLong}
              icon={<ArrowRightIcon />}
              onClick={handleSubmit}
              size="small"
              variant="subtle"
            />
          </span>
        </div>
        <NarrativesChatInputFooter
          capacityTracker={capacityTracker}
          centered
          isInputTooLong={isInputTooLong}
        />
        <p>Need inspiration?</p>
        <BannerButton
          className={styles['chat-banner-button']}
          data-testid="narratives-chat-landing-page-templates-button"
          leadingIcon={<GridRightIcon />}
          onClick={() => onNavigation('templates')}
          text="Browse Narrative Templates"
        />
      </div>
      <Footer />
    </Layout.Column>
  );
};

NarrativesChatLandingPage.propTypes = {
  capacityTracker: PropTypes.node,
  isCapacityExhausted: PropTypes.bool,
  onNavigation: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

NarrativesChatLandingPage.defaultProps = {
  capacityTracker: null,
  isCapacityExhausted: false,
};

export default NarrativesChatLandingPage;
